/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import * as Cesium from "cesium";
import { useCallback, useRef } from "react";
import { CesiumTool } from "~/redux/reducers/mapReducer";
import { CesiumEventType } from "./useCesiumEventHandler";

type UseCesiumMeasurementToolReturn = {
  cesiumMeasurementTool: (args: CesiumMeasurementToolArgs) => void;
};

type CesiumMeasurementToolArgs = {
  ellipsoid: Cesium.Ellipsoid;
  viewer: Cesium.Viewer;
  position: Cesium.Cartesian2;
  activeTool?: CesiumTool;
  eventType: CesiumEventType;
};

export function useCesiumMeasurementTool(): UseCesiumMeasurementToolReturn {
  const measPointsRef = useRef<Cesium.PointPrimitiveCollection>();
  const measLinesRef = useRef<Cesium.PolylineCollection>();
  const distanceLabelRef = useRef<Cesium.Entity>();
  const verticalLabelRef = useRef<Cesium.Entity>();
  const horizontalLabelRef = useRef<Cesium.Entity>();
  const hoverPointRef = useRef<Cesium.Entity>();
  const bAngleRef = useRef<Cesium.Entity>();
  const cAngleRef = useRef<Cesium.Entity>();

  const getMidpoint = useCallback(
    (p1: Cesium.Cartographic, p2: Cesium.Cartographic, height: number) => {
      const geodesic = new Cesium.EllipsoidGeodesic();
      geodesic.setEndPoints(p1, p2);
      const midpointCarto = geodesic.interpolateUsingFraction(0.5);
      return Cesium.Cartesian3.fromRadians(
        midpointCarto.longitude,
        midpointCarto.latitude,
        height,
      );
    },
    [],
  );

  const getHorizontalDistance = useCallback(
    (
      p1: Cesium.Cartographic,
      p2: Cesium.Cartographic,
      onlyValue = false,
    ): string => {
      const geodesic = new Cesium.EllipsoidGeodesic();
      geodesic.setEndPoints(p1, p2);
      const meters = geodesic.surfaceDistance;
      if (onlyValue) return meters.toString();
      if (meters > 1000) {
        return `${(meters / 1000).toFixed(2)} km`;
      }
      return `${meters.toFixed(2)} m`;
    },
    [],
  );

  const getDistance = useCallback(
    (
      p1: Cesium.Cartographic,
      p2: Cesium.Cartographic,
      p1geo: Cesium.Cartographic,
      p2geo: Cesium.Cartographic,
      onlyValue = false,
    ): string => {
      const geodesic = new Cesium.EllipsoidGeodesic();
      geodesic.setEndPoints(p1, p2);
      const horizMeters = geodesic.surfaceDistance;
      const heights = [p1geo.height, p2geo.height];
      const vertiMeters = Math.max(...heights) - Math.min(...heights);
      // Pythagoras yay
      const meters = (horizMeters ** 2 + vertiMeters ** 2) ** 0.5;
      if (onlyValue) return meters.toString();
      if (meters > 1000) {
        return `${(meters / 1000).toFixed(2)} km`;
      }
      return `${meters.toFixed(2)} m`;
    },
    [],
  );

  const getVerticalDistance = useCallback(
    (
      p1: Cesium.Cartographic,
      p2: Cesium.Cartographic,
      onlyValue = false,
    ): string => {
      const heights = [p1.height, p2.height];
      const meters = Math.max(...heights) - Math.min(...heights);
      if (onlyValue) return meters.toString();
      if (meters > 1000) {
        return `${(meters / 1000).toFixed(2)} km`;
      }
      return `${meters.toFixed(2)} m`;
    },
    [],
  );

  // Utility fns
  const addDistanceLabel = useCallback(
    (
      p1: Cesium.PointPrimitive,
      p2: Cesium.PointPrimitive,
      labelZ: number,
      ellipsoid: Cesium.Ellipsoid,
      viewer: Cesium.Viewer,
      heightRef: Cesium.HeightReference,
      tool: CesiumTool,
    ) => {
      const point1 = {
        point: p1,
        cartographic: ellipsoid.cartesianToCartographic(p1.position),
        longitude: Cesium.Math.toDegrees(p1.position.x),
        latitude: Cesium.Math.toDegrees(p1.position.y),
      };
      const point2 = {
        point: p2,
        cartographic: ellipsoid.cartesianToCartographic(p2.position),
        longitude: Cesium.Math.toDegrees(p2.position.x),
        latitude: Cesium.Math.toDegrees(p2.position.y),
      };
      const point1GeoPosition = Cesium.Cartographic.fromCartesian(
        point1.point.position,
      );
      const point2GeoPosition = Cesium.Cartographic.fromCartesian(
        point2.point.position,
      );

      const label: Partial<Cesium.Label> = {
        showBackground: true,
        horizontalOrigin: Cesium.HorizontalOrigin.CENTER,
        verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
        pixelOffset: new Cesium.Cartesian2(0, 0),
        eyeOffset: new Cesium.Cartesian3(0, 0, -5),
        fillColor: Cesium.Color.WHITE,
        font: "18px sans-serif",
        backgroundColor: new Cesium.Color(0.165, 0.165, 0.165, 0.9),
        heightReference: heightRef,
      };
      if (tool === "measurement") {
        horizontalLabelRef.current = viewer.entities.add({
          position: getMidpoint(
            point1.cartographic,
            point2.cartographic,
            point1GeoPosition.height,
          ),
          label: {
            ...label,
            text: getHorizontalDistance(
              point1.cartographic,
              point2.cartographic,
            ),
          },
        });
      }

      distanceLabelRef.current = viewer.entities.add({
        position: getMidpoint(point1.cartographic, point2.cartographic, labelZ),
        label: {
          ...label,
          text: getDistance(
            point1.cartographic,
            point2.cartographic,
            point1GeoPosition,
            point2GeoPosition,
          ),
        },
      });

      if (tool === "measurement") {
        verticalLabelRef.current = viewer.entities.add({
          position: getMidpoint(
            point2.cartographic,
            point2.cartographic,
            labelZ,
          ),
          label: {
            ...label,
            text: getVerticalDistance(point1GeoPosition, point2GeoPosition),
          },
        });
      }

      // Angles
      // a is always 90°
      // b is the angle between hypotenuse (distance) and vertical line
      // c is the angle between hypotonesu (distance) and horizontal line
      // const aAngleDegrees = 90;
      const bAngleDegrees = Cesium.Math.toDegrees(
        Math.acos(
          Number.parseFloat(
            getVerticalDistance(point1GeoPosition, point2GeoPosition, true),
          ) /
            Number.parseFloat(
              getDistance(
                point1.cartographic,
                point2.cartographic,
                point1GeoPosition,
                point2GeoPosition,
                true,
              ),
            ),
        ),
      );
      const cAngleDegrees = Cesium.Math.toDegrees(
        Math.acos(
          Number.parseFloat(
            getHorizontalDistance(
              point1.cartographic,
              point2.cartographic,
              true,
            ),
          ) /
            Number.parseFloat(
              getDistance(
                point1.cartographic,
                point2.cartographic,
                point1GeoPosition,
                point2GeoPosition,
                true,
              ),
            ),
        ),
      );
      if (tool === "measurement") {
        bAngleRef.current = viewer.entities.add({
          position: p2.position,
          label: {
            ...label,
            text: `${bAngleDegrees.toFixed(2)}°`,
          },
        });
      }

      cAngleRef.current = viewer.entities.add({
        position: p1.position,
        label: {
          ...label,
          text: `${cAngleDegrees.toFixed(2)}°`,
        },
      });
    },
    [getDistance, getHorizontalDistance, getMidpoint, getVerticalDistance],
  );

  const cesiumMeasurementTool = useCallback(
    ({
      ellipsoid,
      viewer,
      position,
      activeTool,
      eventType,
    }: CesiumMeasurementToolArgs) => {
      const linepointColor = Cesium.Color.RED;
      const pointOutlineColor = Cesium.Color.YELLOW;

      if (activeTool !== "measurement" && activeTool !== "simpleMeasurement") {
        measPointsRef.current?.removeAll();
        measLinesRef.current?.removeAll();
        if (distanceLabelRef.current) {
          viewer.entities.remove(distanceLabelRef.current);
        }
        if (horizontalLabelRef.current) {
          viewer.entities.remove(horizontalLabelRef.current);
        }
        if (verticalLabelRef.current) {
          viewer.entities.remove(verticalLabelRef.current);
        }
        if (hoverPointRef.current) {
          viewer.entities.remove(hoverPointRef.current);
        }
        if (bAngleRef.current) {
          viewer.entities.remove(bAngleRef.current);
        }
        if (cAngleRef.current) {
          viewer.entities.remove(cAngleRef.current);
        }
        measPointsRef.current = undefined;
        measLinesRef.current = undefined;
        distanceLabelRef.current = undefined;
        horizontalLabelRef.current = undefined;
        verticalLabelRef.current = undefined;
        hoverPointRef.current = undefined;
        bAngleRef.current = undefined;
        cAngleRef.current = undefined;
        return;
      }

      // Simple meas has only some entities
      if (activeTool === "simpleMeasurement") {
        if (horizontalLabelRef.current) {
          viewer.entities.remove(horizontalLabelRef.current);
        }
        if (verticalLabelRef.current) {
          viewer.entities.remove(verticalLabelRef.current);
        }
        if (hoverPointRef.current) {
          viewer.entities.remove(hoverPointRef.current);
        }
        if (bAngleRef.current) {
          viewer.entities.remove(bAngleRef.current);
        }
        horizontalLabelRef.current = undefined;
        verticalLabelRef.current = undefined;
        hoverPointRef.current = undefined;
        bAngleRef.current = undefined;
      }

      // Measure entity/object or globe tile
      let carte3: Cesium.Cartesian3 | undefined;
      let pickedFromGlobe = false;
      carte3 = viewer.scene.pickPosition(position);
      const feature = viewer.scene.pick(position);
      let featureExists = false;
      if (feature && feature.primitive instanceof Cesium.Cesium3DTileset) {
        featureExists = true;
      }
      if (!carte3 && !featureExists) {
        carte3 = viewer.scene.globe.pick(
          viewer.camera.getPickRay(position),
          viewer.scene,
        );
        pickedFromGlobe = true;
      }
      const heightRef = pickedFromGlobe
        ? Cesium.HeightReference.CLAMP_TO_GROUND
        : Cesium.HeightReference.NONE;

      if (eventType === "hover" && carte3) {
        if (!hoverPointRef.current) {
          hoverPointRef.current = viewer.entities.add({
            position: carte3,
            point: {
              color: linepointColor,
              pixelSize: 10,
              outlineWidth: 3,
              outlineColor: pointOutlineColor,
              // heightReference: heightRef,
            },
          });
        } else {
          hoverPointRef.current.position = new Cesium.ConstantPositionProperty(
            carte3,
          );
        }
      }

      if (!position || eventType !== "click") return;
      // Initialize refs
      if (!measPointsRef.current) {
        measPointsRef.current = viewer.scene.primitives.add(
          new Cesium.PointPrimitiveCollection(),
        );
      }
      if (!measLinesRef.current) {
        measLinesRef.current = viewer.scene.primitives.add(
          new Cesium.PolylineCollection(),
        );
      }

      if (!carte3) return;
      // Measurement is full, remove all labels and lines
      if (measPointsRef.current?.length === 2) {
        measPointsRef.current.removeAll();
        measLinesRef.current?.removeAll();
        if (distanceLabelRef.current) {
          viewer.entities.remove(distanceLabelRef.current);
        }
        if (horizontalLabelRef.current) {
          viewer.entities.remove(horizontalLabelRef.current);
        }
        if (verticalLabelRef.current) {
          viewer.entities.remove(verticalLabelRef.current);
        }
        if (bAngleRef.current) {
          viewer.entities.remove(bAngleRef.current);
        }
        if (cAngleRef.current) {
          viewer.entities.remove(cAngleRef.current);
        }
      }
      // Add first point
      if (measPointsRef.current?.length === 0) {
        measPointsRef.current.add({
          position: carte3,
          color: linepointColor,
          outlineWidth: 1,
          outlineColor: pointOutlineColor,
          heightReference: heightRef,
        });
      }
      // Add second point and connect with lines
      else if (measPointsRef.current?.length === 1) {
        const p2 = measPointsRef.current.add({
          position: carte3,
          color: linepointColor,
          outlineWidth: 1,
          outlineColor: pointOutlineColor,
          heightReference: heightRef,
        });
        const p1 = measPointsRef.current.get(0);
        if (!p1) return;
        const p1Carto = Cesium.Cartographic.fromCartesian(p1.position);
        const p2Carto = Cesium.Cartographic.fromCartesian(p2.position);
        // Create lines between points
        // Line 1 is the hypotenuse, distance from point1 to point2
        // Line 2 is straight from point2 to height of point1
        // Line 3 is from point1 to end of line2
        const line1Pos = [
          Cesium.Cartesian3.fromRadians(
            p1Carto.longitude,
            p1Carto.latitude,
            p1Carto.height,
          ),
          Cesium.Cartesian3.fromRadians(
            p2Carto.longitude,
            p2Carto.latitude,
            p2Carto.height,
          ),
        ];
        const line2Pos = [
          Cesium.Cartesian3.fromRadians(
            p2Carto.longitude,
            p2Carto.latitude,
            p2Carto.height,
          ),
          Cesium.Cartesian3.fromRadians(
            p2Carto.longitude,
            p2Carto.latitude,
            p1Carto.height,
          ),
        ];
        const line3Pos = [
          Cesium.Cartesian3.fromRadians(
            p1Carto.longitude,
            p1Carto.latitude,
            p1Carto.height,
          ),
          Cesium.Cartesian3.fromRadians(
            p2Carto.longitude,
            p2Carto.latitude,
            p1Carto.height,
          ),
        ];
        measLinesRef.current?.add({
          id: 1,
          show: true,
          positions: line1Pos,
          width: 1,
          heightReference: heightRef,
          material: new Cesium.Material({
            fabric: {
              type: "Color",
              uniforms: {
                color: linepointColor,
              },
            },
          }),
        });
        measLinesRef.current?.add({
          id: 2,
          show: activeTool === "measurement",
          positions: line2Pos,
          width: 1,
          heightReference: heightRef,
          material: new Cesium.Material({
            fabric: {
              type: "PolylineDash",
              uniforms: {
                color: linepointColor,
              },
            },
          }),
        });
        measLinesRef.current?.add({
          id: 3,
          show: activeTool === "measurement",
          positions: line3Pos,
          width: 1,
          heightReference: heightRef,
          material: new Cesium.Material({
            fabric: {
              type: "PolylineDash",
              uniforms: {
                color: linepointColor,
              },
            },
          }),
        });
        let labelZ: number;
        if (p2Carto.height >= p1Carto.height) {
          labelZ = p1Carto.height + (p2Carto.height - p1Carto.height) / 2;
        } else {
          labelZ = p2Carto.height + (p1Carto.height - p2Carto.height) / 2;
        }
        addDistanceLabel(
          p1,
          p2,
          labelZ,
          ellipsoid,
          viewer,
          heightRef,
          activeTool,
        );
      }
    },
    [addDistanceLabel],
  );

  return { cesiumMeasurementTool };
}
